/**
 * Surfaces task proposals as toasts.
 *
 * Lives at the app shell so a proposal can land on any screen.
 *
 *   1. On mount and on a `synfolio:proposal-changed` window event,
 *      hit GET /api/tasks?status=proposed.
 *   2. For each proposal the watcher hasn't yet shown, fire a toast
 *      with Accept / Pick another / Always-for-this-level actions.
 *      The toast id is the task id, so a re-fetch replaces the
 *      existing card (no flicker, no stacking).
 *   3. Polling cadence: every 60s when the tab is visible. Cheap,
 *      keeps "just-arrived-from-another-device" cases working.
 *
 * The window event `synfolio:proposal-changed` is dispatched by
 * createPersonalTask() / acceptTaskProposal() / etc. so the UI
 * round-trip is instant after the user's own action. The poll is
 * a backstop for changes that happened elsewhere.
 */

import { useEffect, useRef } from 'react';
import { api, type PersonalTask, type SchedulingPref } from '../api';
import { useToast } from './Toast';
import { useRouter } from '../router';

const POLL_MS = 60_000;

export function ProposalWatcher() {
  const { toast } = useToast();
  const { navigate } = useRouter();
  const seenRef = useRef(new Set<string>());

  useEffect(() => {
    let cancelled = false;

    async function check(): Promise<void> {
      try {
        const [tasksRes, prefsRes] = await Promise.all([
          api.listPersonalTasks('proposed'),
          api.getSchedulingPrefs(),
        ]);
        if (cancelled) return;
        const prefByLevel = new Map(prefsRes.prefs.map((p) => [p.workLevel, p] as const));
        for (const t of tasksRes.tasks) {
          if (seenRef.current.has(t.id)) continue;
          seenRef.current.add(t.id);
          surface(t, prefByLevel.get(t.workLevel) ?? null);
        }
        // Drop ids that no longer have a proposed status so a future
        // proposal for the same task does surface again.
        const stillProposed = new Set(tasksRes.tasks.map((t) => t.id));
        for (const id of seenRef.current) {
          if (!stillProposed.has(id)) seenRef.current.delete(id);
        }
      } catch {
        /* swallow; the watcher is best-effort */
      }
    }

    function surface(task: PersonalTask, pref: SchedulingPref | null): void {
      toast({
        id: `proposal:${task.id}`,
        tone: 'accent',
        title: (
          <span>
            Proposed: <span className="font-semibold">{task.title}</span>
          </span>
        ),
        body: (
          <span>
            {formatProposal(task)} · {labelFor(task.workLevel)}{' '}
            <span className="text-cream-4">({task.estimatedMinutes} min)</span>
          </span>
        ),
        actions: [
          {
            label: 'Accept',
            variant: 'primary',
            onClick: async () => {
              try {
                await api.acceptTaskProposal(task.id);
                window.dispatchEvent(new Event('synfolio:tasks-changed'));
              } catch {
                /* swallow */
              }
            },
          },
          {
            label: 'Pick another',
            onClick: async () => {
              try {
                const r = await api.reschedulePersonalTask(task.id);
                if (r.found && r.task) {
                  seenRef.current.delete(task.id);
                  surface(r.task, pref);
                  return { keepOpen: true };
                }
                toast({
                  tone: 'warning',
                  title: 'No other slots fit',
                  body: 'Try widening your working hours or shrinking the task.',
                });
                window.dispatchEvent(new Event('synfolio:tasks-changed'));
                return {};
              } catch {
                /* swallow */
                return {};
              }
            },
          },
          // The "always do this for X work" affordance — the user's
          // own words. Updates the pref so the next task at this level
          // schedules silently.
          ...(pref && pref.mode !== 'always'
            ? [
                {
                  label: `Always for ${labelFor(task.workLevel)}`,
                  onClick: async () => {
                    try {
                      // Accept this one too, since the user just
                      // confirmed they trust the scheduler at this
                      // level. Saves a click and matches the spirit
                      // of "always".
                      await Promise.all([
                        api.acceptTaskProposal(task.id),
                        api.putSchedulingPrefs([{ ...pref, mode: 'always' }]),
                      ]);
                      window.dispatchEvent(new Event('synfolio:tasks-changed'));
                    } catch {
                      /* swallow */
                    }
                  },
                },
              ]
            : []),
          {
            label: 'Open',
            onClick: () => {
              navigate('/calendar');
            },
          },
        ],
      });
    }

    void check();
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') void check();
    }, POLL_MS);
    const onChange = () => void check();
    window.addEventListener('synfolio:proposal-changed', onChange);
    window.addEventListener('synfolio:tasks-changed', onChange);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener('synfolio:proposal-changed', onChange);
      window.removeEventListener('synfolio:tasks-changed', onChange);
    };
  }, [toast, navigate]);

  return null;
}

function labelFor(level: PersonalTask['workLevel']): string {
  if (level === 'deep') return 'Deep work';
  if (level === 'admin') return 'Admin';
  return 'Shallow work';
}

function formatProposal(task: PersonalTask): string {
  if (!task.scheduledStartsAt) return 'When you have time';
  const d = new Date(task.scheduledStartsAt * 1000);
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
